import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { match } from 'ts-pattern';
import { QueryToSqlHelper } from '../../../api/query-to-sql-helper';
import {
  CohortMetricId,
  EmployeeCohortMetricId,
  MetricId,
  MetricType,
  RegularMetricId,
} from '../../api/types-graphql';
import { Filter } from '../../common/components/filter/filterbar/types';
import { MetricTypes } from '../../common/types';
import {
  useMetricsServiceContext,
  useMetricTreeContext,
} from '../../context/contexts';
import { Hasher } from '../hasher';
import { hashString, QueryMetricsQuerySuccess, VersionId } from '../types';
import { toRegularFrontendType } from '../utils';
import { MetricsQueryService } from './metrics-query-service';
import {
  metricTreeToMetricCategoryDetailsMap,
  metricTreeToMetricDetailsMap,
  metricTreeToMetricGroupDetailsMap,
} from './utils';

export const useMetricTreeQuery = (debugString: string) => {
  const metricService = useMetricsServiceContext();
  const key = ['get-metric-tree', ...metricService.getDeps()];
  return {
    queryKey: key,
    queryFn: () => {
      console.debug(`[useQuery] ${debugString}`, key);
      const metricTree = metricService.getMetricTree();
      return metricTree;
    },
  };
};

export const useMetricDetailsMap = () => {
  const metricTree = useMetricTreeContext();
  return metricTreeToMetricDetailsMap(metricTree);
};

export const useMetricGroupDetailsMap = () => {
  const metricTree = useMetricTreeContext();
  return metricTreeToMetricGroupDetailsMap(metricTree);
};

export const useMetricCategoryDetailsMap = () => {
  const metricTree = useMetricTreeContext();
  return metricTreeToMetricCategoryDetailsMap(metricTree);
};

export const useQueryMetricExample = (
  metricService: MetricsQueryService,
  metricId: MetricId
) => {
  const metricDetails = useMetricDetailsMap()[metricId];
  if (!metricDetails) {
    throw new Error(`Metric details not found for metric id ${metricId}`);
  }
  const metricType = metricDetails.metricType;
  return useSuspenseQuery({
    queryKey: ['query-metric-example', metricId, ...metricService.getDeps()],
    queryFn: async () => {
      const timeSelection = {
        calendarYearMonthly: { start: '2024-01-01', end: '2024-02-29' },
        calendarYearQuarterly: null,
        calendarYearYearly: null,
        financialYearQuarterly: null,
        financialYearYearly: null,
        singleValue: null,
        singleValueByCalendarMonths: null,
        singleValueByCalendarYears: null,
        singleValueByFinancialQuarters: null,
        singleValueByFinancialYears: null,
      };

      return match(metricType)
        .with(MetricType.MetricTypeRegular, async () => {
          return {
            query: (
              await metricService.queryRegularMetrics({
                timeSelection,
                metrics: [
                  {
                    type: 'RegularMetricId',
                    value: metricId as unknown as RegularMetricId,
                  },
                ],
              })
            ).queryMetrics,
          };
        })
        .with(MetricType.MetricTypeCohort, async () => {
          return {
            query: (
              await metricService.queryCohortMetrics({
                timeSelection,
                metrics: [
                  {
                    type: 'CohortMetricId',
                    value: metricId as unknown as CohortMetricId,
                  },
                ],
              })
            ).queryCohortMetrics,
          };
        })
        .with(MetricType.MetricTypeEmployeeCohort, async () => {
          return {
            query: (
              await metricService.queryEmployeeCohortMetrics({
                timeSelection,
                metrics: [
                  {
                    type: 'EmployeeCohortMetricId',
                    value: metricId as unknown as EmployeeCohortMetricId,
                  },
                ],
              })
            ).queryEmployeeCohortMetrics,
          };
        })
        .exhaustive();
    },
  });
};

export const useHeadcountAtDate = (
  metricService: MetricsQueryService,
  endDate: VersionId,
  filters: Filter[]
) => {
  return useQuery({
    queryKey: [
      'headcount-at-date',
      endDate,
      hashString([Hasher.hash(filters), ...metricService.getDeps()]),
    ],
    queryFn: async () => {
      console.log('useHeadcountAtDate query');
      const result = await metricService.queryRegularMetrics({
        timeSelection: {
          singleValueByCalendarMonths: { start: endDate, end: endDate },
          calendarYearMonthly: null,
          calendarYearQuarterly: null,
          calendarYearYearly: null,
          financialYearQuarterly: null,
          financialYearYearly: null,
          singleValue: null,
          singleValueByCalendarYears: null,
          singleValueByFinancialYears: null,
          singleValueByFinancialQuarters: null,
        },
        metrics: [
          {
            type: 'RegularMetricId',
            value: RegularMetricId.Pm0001_1IndividualsCount,
          },
        ],
        userFilters:
          QueryToSqlHelper.getFilterConditions(
            filters,
            MetricTypes.REGULAR,
            false
          ) ?? undefined,
      });
      console.log('useHeadcountAtDate query done');
      return match(result.queryMetrics?.results[0])
        .with(
          { __typename: 'MetricResultSuccess' },
          (success) =>
            toRegularFrontendType(
              success as unknown as QueryMetricsQuerySuccess
            ).segments[0].groupSegments[0].data
        )
        .with({ __typename: 'MetricResultFailure' }, (error) => {
          console.log('MetricResultFailure');
          return new Error(error.message);
        })
        .otherwise(() => {
          console.log('Unexpected results');
          return new Error('Unexpected results');
        });
    },
  });
};
